import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import axios from "axios";
import { toast } from "sonner";
import { 
  ImageIcon, 
  Plus, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  RefreshCw,
  BookOpen,
  Upload,
  FileText,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Download,
  Trash2
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lazy loading image component
const LazyImage = ({ imageId, alt }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageId) {
      setLoading(false);
      setError("No image ID");
      return;
    }

    let cancelled = false;

    const fetchImage = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No auth token");
        }
        const response = await axios.get(`${API}/cover-images/${imageId}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        });
        if (!cancelled && response.data && response.data.image_data) {
          setImageData(response.data.image_data);
        } else if (!cancelled) {
          setError("No image data");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchImage();
    
    return () => { cancelled = true; };
  }, [imageId]);

  if (!imageId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-stone-100">
        <div className="text-center text-stone-400">
          <AlertCircle className="h-6 w-6 mx-auto mb-1" />
          <span className="text-xs">No image</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-stone-100">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error || !imageData) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-stone-100">
        <div className="text-center text-stone-400">
          <AlertCircle className="h-6 w-6 mx-auto mb-1" />
          <span className="text-xs">Failed</span>
        </div>
      </div>
    );
  }

  return (
    <img 
      src={`data:image/png;base64,${imageData}`} 
      alt={alt}
      className="w-full h-full object-cover"
    />
  );
};

const CoverCreator = () => {
  const navigate = useNavigate();
  const [coverProjects, setCoverProjects] = useState([]);
  const [completedBooks, setCompletedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  
  // Dialog states
  const [showNewCover, setShowNewCover] = useState(false);
  const [showCoverDetail, setShowCoverDetail] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateDetails, setRegenerateDetails] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [generatingProject, setGeneratingProject] = useState(null);
  
  // New flow states
  const [coverSource, setCoverSource] = useState(""); // "dtr" or "imported"
  const [step, setStep] = useState(1); // 1 = source selection, 2 = details form
  const [selectedDTRBook, setSelectedDTRBook] = useState(null);
  
  // Form states
  const [title, setTitle] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [genre, setGenre] = useState("");
  const [mood, setMood] = useState("");
  const [customDetails, setCustomDetails] = useState("");
  const [backCoverType, setBackCoverType] = useState("summary");
  const [backCoverContent, setBackCoverContent] = useState("");
  const [bookContent, setBookContent] = useState("");
  const [creating, setCreating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const genres = [
    "Fiction", "Non-Fiction", "Romance", "Thriller", "Mystery", 
    "Sci-Fi", "Fantasy", "Horror", "Biography", "Self-Help",
    "Business", "History", "Children&apos;s", "Young Adult", "Poetry"
  ];

  const moods = [
    "Dark & Moody", "Bright & Cheerful", "Minimalist", "Dramatic",
    "Elegant", "Vintage", "Modern", "Whimsical", "Professional",
    "Mysterious", "Romantic", "Epic", "Clean & Simple"
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh when viewing a generating project
  useEffect(() => {
    let interval;
    if (showCoverDetail && selectedProject?.status === "generating") {
      interval = setInterval(() => {
        refreshProject(selectedProject.id);
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showCoverDetail, selectedProject?.status, selectedProject?.id]);

  // Auto-refresh list when there are generating projects
  useEffect(() => {
    let interval;
    const hasGenerating = coverProjects.some(p => p.status === "generating");
    if (hasGenerating && !showCoverDetail) {
      interval = setInterval(() => {
        loadData();
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [coverProjects, showCoverDetail]);

  // Poll for generating project progress
  useEffect(() => {
    let interval;
    if (generatingProject && generatingProject.status === "generating") {
      interval = setInterval(async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(`${API}/covers/${generatingProject.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.status === "completed") {
            setGeneratingProject(null);
            toast.success("Cover generation complete! Click to view your covers.");
            await loadData();
          } else if (response.data.status === "error") {
            setGeneratingProject(prev => ({ ...prev, status: "error", error: response.data.error_message }));
          } else {
            setGeneratingProject(prev => ({ 
              ...prev, 
              progress: response.data.progress || "Generating..."
            }));
          }
        } catch (error) {
          console.error("Failed to check progress:", error);
        }
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [generatingProject]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [coversRes, booksRes, creditsRes] = await Promise.all([
        axios.get(`${API}/covers`, config),
        axios.get(`${API}/books/completed`, config),
        axios.get(`${API}/credits`, config)
      ]);
      
      setCoverProjects(coversRes.data || []);
      setCompletedBooks(booksRes.data || []);
      setUserCredits(creditsRes.data?.credits || 0);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCredits = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserCredits(response.data?.credits || 0);
      return response.data?.credits || 0;
    } catch (error) {
      console.error("Failed to fetch credits:", error);
      return userCredits;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['.txt', '.doc', '.docx', '.pdf'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error("Please upload a .txt, .doc, .docx, or .pdf file");
      return;
    }

    setUploadingFile(true);
    setUploadedFile(file);

    try {
      // For .txt files, read directly
      if (fileExtension === '.txt') {
        const text = await file.text();
        setBookContent(text);
        
        // Try to extract title from first line if not set
        if (!title) {
          const firstLine = text.split('\n')[0].trim();
          if (firstLine && firstLine.length < 100) {
            setTitle(firstLine);
          }
        }
        toast.success(`File loaded: ${file.name}`);
      } else {
        // For .doc, .docx, .pdf - upload to backend for processing
        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem("token");
        const response = await axios.post(`${API}/upload/extract-text`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.content) {
          setBookContent(response.data.content);
          if (response.data.title && !title) {
            setTitle(response.data.title);
          }
          toast.success(`File processed: ${file.name}`);
        }
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to process file. Please try a .txt file or paste content manually.");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSelectDTRBook = (bookId) => {
    const book = completedBooks.find(b => b.id === bookId);
    if (book) {
      setSelectedDTRBook(book);
      setTitle(book.title);
      setGenre(book.genre || "");
      // Auto-set back cover to summary from this book
      setBackCoverType("summary");
    }
  };

  const handleSourceSelect = (source) => {
    setCoverSource(source);
    if (source === "dtr" && completedBooks.length === 0) {
      toast.error("No completed DTR books found. Please use External Book option.");
      return;
    }
    setStep(2);
  };

  const handleCreateCover = async () => {
    if (!title.trim()) {
      toast.error("Please enter a book title");
      return;
    }
    if (!authorName.trim()) {
      toast.error("Please enter the author name");
      return;
    }
    if (!genre) {
      toast.error("Please select a genre");
      return;
    }
    if (!mood) {
      toast.error("Please select a mood/style");
      return;
    }
    if (userCredits < 1) {
      toast.error("Insufficient credits. You need 1 credit for cover creation.");
      return;
    }
    if (backCoverType === "bio" && !backCoverContent.trim()) {
      toast.error("Please enter your author bio");
      return;
    }
    if (backCoverType === "custom" && !backCoverContent.trim()) {
      toast.error("Please enter your custom back cover text");
      return;
    }
    if (coverSource === "imported" && backCoverType === "summary" && !bookContent.trim()) {
      toast.error("Please paste your book content for the summary");
      return;
    }

    setCreating(true);
    
    try {
      const token = localStorage.getItem("token");
      
      const requestData = {
        title,
        author_name: authorName,
        genre,
        mood,
        custom_details: customDetails || null,
        back_cover_type: backCoverType,
        back_cover_content: backCoverType !== "summary" ? backCoverContent : null,
        source_book_id: coverSource === "dtr" && selectedDTRBook ? selectedDTRBook.id : null,
        book_content: coverSource === "imported" && backCoverType === "summary" ? bookContent : null
      };
      
      const response = await axios.post(
        `${API}/covers/create`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Set the generating project to show progress immediately
      setGeneratingProject({
        id: response.data.project_id,
        title: title,
        author_name: authorName,
        genre: genre,
        mood: mood,
        status: "generating",
        progress: "Starting cover generation..."
      });
      
      toast.success("Cover generation started! Watch the progress below.");
      setShowNewCover(false);
      resetForm();
      await loadData();
      
    } catch (error) {
      console.error("Cover creation error:", error);
      toast.error(error.response?.data?.detail || "Failed to create covers");
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerate = async (projectId) => {
    console.log("Regenerate clicked for project:", projectId);
    
    if (!projectId) {
      toast.error("No project ID found");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Not logged in");
        return;
      }
      
      // Show the regenerate dialog (regeneration is free)
      setRegenerateDetails("");
      setShowRegenerateDialog(true);
      
    } catch (error) {
      console.error("Regenerate error:", error);
      toast.error("Failed to open regenerate dialog: " + error.message);
    }
  };

  const confirmRegenerate = async () => {
    if (!selectedProject?.id) {
      toast.error("No project selected");
      return;
    }

    setIsRegenerating(true);
    toast.info("Starting regeneration...");
    
    try {
      const token = localStorage.getItem("token");
      
      // Call regenerate API with additional details
      const response = await axios.post(
        `${API}/covers/regenerate`,
        { 
          cover_project_id: selectedProject.id,
          additional_details: regenerateDetails 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log("Regenerate response:", response.data);
      toast.success("Cover regeneration started!");
      
      // Close the regenerate dialog
      setShowRegenerateDialog(false);
      setRegenerateDetails("");
      
      // Update the selected project status
      setSelectedProject(prev => ({ ...prev, status: "generating", progress: "Starting regeneration...", covers: [] }));
      
      // Refresh data
      await loadData();
      
    } catch (error) {
      console.error("Regenerate error:", error);
      toast.error(error.response?.data?.detail || "Failed to regenerate covers: " + error.message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDeleteCover = async (projectId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const confirmed = window.confirm("Delete this cover project? If it was stuck or failed, your credit will be refunded.");
    if (!confirmed) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/covers/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data?.message || "Cover project deleted");
      await loadData();
      
      // Close detail dialog if viewing this project
      if (selectedProject?.id === projectId) {
        setShowCoverDetail(false);
        setSelectedProject(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to delete cover project";
      toast.error(errorMsg);
    }
  };

  const resetForm = () => {
    setCoverSource("");
    setStep(1);
    setSelectedDTRBook(null);
    setTitle("");
    setAuthorName("");
    setGenre("");
    setMood("");
    setCustomDetails("");
    setBackCoverType("summary");
    setBackCoverContent("");
    setBookContent("");
  };

  const handleViewProject = async (project) => {
    setSelectedProject(project);
    setShowCoverDetail(true);
    
    // Refresh credits when viewing a project
    await fetchUserCredits();
    
    // Fetch full details with images
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/covers/${project.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for large image data
      });
      setSelectedProject(response.data);
    } catch (error) {
      console.error("Failed to fetch cover details:", error);
      toast.error("Failed to load cover images. Please try again.");
    }
  };

  const refreshProject = async (projectId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/covers/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(response.data);
      await loadData();
    } catch (error) {
      console.error("Failed to refresh project:", error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Completed</Badge>;
      case "generating":
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating</Badge>;
      case "error":
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" /> Error</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading Cover Creator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 ml-0 md:ml-64 py-12 px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold playfair text-stone-900 mb-2 flex items-center gap-3">
                <ImageIcon className="h-10 w-10 text-amber-600" />
                Cover Creation
              </h1>
              <p className="text-stone-600">
                Generate professional book covers with AI
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="text-lg px-4 py-2">
                {userCredits} Credits
              </Badge>
              <Button 
                onClick={() => setShowNewCover(true)} 
                size="lg"
                className="bg-amber-900 hover:bg-amber-800 text-white rounded-full shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Cover
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="mb-8 border-amber-200 bg-amber-50/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Sparkles className="h-8 w-8 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-stone-900 mb-2">AI-Powered Cover Design</h3>
                  <p className="text-stone-600 mb-3">
                    Create stunning book covers with front, back, and spine designs. 
                    Get 4 unique variations to choose from. Perfect for print publishing (6&quot;x9&quot; format).
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>4 Cover Variations</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Front + Back + Spine</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>1 Credit / 4 Covers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Free to Regenerate</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generation Progress Card */}
          {generatingProject && (
            <Card className="mb-8 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-stone-900 mb-1">
                      Generating: {generatingProject.title}
                    </h3>
                    <p className="text-sm text-stone-600 mb-2">
                      by {generatingProject.author_name} • {generatingProject.genre} • {generatingProject.mood}
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-blue-200 rounded-full h-2 overflow-hidden">
                        <div className="bg-blue-600 h-full rounded-full animate-pulse" style={{ width: '60%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-blue-700">
                        {generatingProject.progress || "Generating..."}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500 mt-2">
                      Creating 4 unique cover variations with front, back, and spine designs...
                    </p>
                  </div>
                  {generatingProject.status === "error" && (
                    <div className="text-red-600">
                      <AlertCircle className="h-6 w-6" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Projects Grid */}
          {coverProjects.length === 0 && !generatingProject ? (
            <Card className="text-center py-16 border-stone-200 shadow-lg">
              <CardContent>
                <ImageIcon className="h-20 w-20 mx-auto text-stone-400 mb-4" />
                <h3 className="text-2xl font-semibold playfair text-stone-900 mb-2">No cover projects yet</h3>
                <p className="text-stone-600 mb-6">
                  Create your first professional book cover
                </p>
                <Button 
                  onClick={() => setShowNewCover(true)}
                  className="bg-amber-900 hover:bg-amber-800 text-white rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Cover
                </Button>
              </CardContent>
            </Card>
          ) : coverProjects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coverProjects.map((project) => (
                <Card 
                  key={project.id} 
                  className="hover:shadow-xl transition-all border-stone-200 cursor-pointer"
                  onClick={() => handleViewProject(project)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl playfair flex items-center gap-2">
                          <ImageIcon className="h-5 w-5 text-amber-600" />
                          {project.title}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          by {project.author_name}
                        </CardDescription>
                      </div>
                      {getStatusBadge(project.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {project.status === "generating" && (
                      <div className="mb-4">
                        <div className="w-full bg-stone-200 rounded-full h-2 mb-1">
                          <div className="bg-amber-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
                        </div>
                        <p className="text-xs text-stone-500">{project.progress || "Generating..."}</p>
                      </div>
                    )}
                    
                    {project.status === "completed" && project.covers?.length > 0 && (
                      <div className="flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg mb-3">
                        <ImageIcon className="h-5 w-5 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">
                          {project.covers.length} cover variation{project.covers.length > 1 ? 's' : ''} ready
                        </span>
                        <span className="text-xs text-amber-600">• Click to view</span>
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-stone-600">Genre:</span>
                        <span className="font-medium">{project.genre}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-stone-600">Style:</span>
                        <span className="font-medium">{project.mood}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-stone-600">Variations:</span>
                        <span className="font-medium">{project.covers?.length || 0}</span>
                      </div>
                    </div>
                    
                    {/* Delete button for stuck/error covers */}
                    {(project.status === "generating" || project.status === "error") && (
                      <div className="mt-4 pt-3 border-t">
                        <button
                          type="button"
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-md transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteCover(project.id, e);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Stuck Cover
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}

          {/* New Cover Dialog - Updated Flow */}
          <Dialog open={showNewCover} onOpenChange={(open) => {
            setShowNewCover(open);
            if (!open) resetForm();
          }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-2">
                  <ImageIcon className="h-6 w-6 text-amber-600" />
                  Create New Cover
                </DialogTitle>
                <DialogDescription>
                  {step === 1 ? "Select your book source" : "Configure your cover design (1 credit)"}
                </DialogDescription>
              </DialogHeader>
              
              {/* Step 1: Source Selection */}
              {step === 1 && (
                <div className="space-y-6 py-4">
                  <p className="text-center text-stone-600">Where is your book from?</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* DTR Book Option */}
                    <Card 
                      className={`cursor-pointer transition-all hover:shadow-lg hover:border-amber-400 ${
                        completedBooks.length === 0 ? 'opacity-50' : ''
                      }`}
                      onClick={() => handleSourceSelect("dtr")}
                    >
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <BookOpen className="h-8 w-8 text-amber-700" />
                        </div>
                        <h3 className="text-lg font-semibold text-stone-900 mb-2">DTR Book</h3>
                        <p className="text-sm text-stone-600 mb-3">
                          Select from your completed DTR books
                        </p>
                        <div className="text-xs text-stone-500">
                          {completedBooks.length > 0 ? (
                            <span className="text-green-600">{completedBooks.length} book(s) available</span>
                          ) : (
                            <span className="text-red-500">No completed books</span>
                          )}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-1 justify-center">
                          <Badge variant="outline" className="text-xs">Auto-fill title</Badge>
                          <Badge variant="outline" className="text-xs">Auto-fill genre</Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Imported Book Option */}
                    <Card 
                      className="cursor-pointer transition-all hover:shadow-lg hover:border-amber-400"
                      onClick={() => handleSourceSelect("imported")}
                    >
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="h-8 w-8 text-stone-700" />
                        </div>
                        <h3 className="text-lg font-semibold text-stone-900 mb-2">External Book</h3>
                        <p className="text-sm text-stone-600 mb-3">
                          For books not created with DTR
                        </p>
                        <div className="text-xs text-stone-500">
                          <span className="text-green-600">Enter book details manually</span>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-1 justify-center">
                          <Badge variant="outline" className="text-xs">Full control</Badge>
                          <Badge variant="outline" className="text-xs">Any book</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* Step 2: Details Form */}
              {step === 2 && (
                <div className="space-y-6">
                  {/* Back Button */}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setStep(1);
                      setSelectedDTRBook(null);
                      setTitle("");
                      setGenre("");
                    }}
                    className="mb-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to source selection
                  </Button>

                  {/* Source Badge */}
                  <div className="flex items-center gap-2">
                    <Badge className={coverSource === "dtr" ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-800"}>
                      {coverSource === "dtr" ? (
                        <><BookOpen className="h-3 w-3 mr-1" /> DTR Book</>
                      ) : (
                        <><FileText className="h-3 w-3 mr-1" /> External Book</>
                      )}
                    </Badge>
                  </div>

                  {/* DTR Book Selector */}
                  {coverSource === "dtr" && (
                    <div className="space-y-2 p-4 border rounded-lg bg-amber-50">
                      <Label>Select Your DTR Book *</Label>
                      <Select 
                        value={selectedDTRBook?.id || ""} 
                        onValueChange={handleSelectDTRBook}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a completed book..." />
                        </SelectTrigger>
                        <SelectContent>
                          {completedBooks.map((book) => (
                            <SelectItem key={book.id} value={book.id}>
                              {book.title} ({book.genre || "No genre"})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedDTRBook && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Title and genre auto-filled from selected book
                        </p>
                      )}
                    </div>
                  )}

                  {/* External Book - File Upload */}
                  {coverSource === "imported" && (
                    <div className="space-y-4 p-4 border rounded-lg bg-stone-50">
                      <div>
                        <Label className="text-base font-semibold">Import Your Book</Label>
                        <p className="text-sm text-stone-600 mt-1">
                          Upload your book file or paste content below to generate a back cover summary
                        </p>
                      </div>
                      
                      {/* File Upload */}
                      <div className="space-y-2">
                        <Label htmlFor="book-file">Upload Book File (Optional)</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            id="book-file"
                            accept=".txt,.doc,.docx,.pdf"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => document.getElementById('book-file').click()}
                            disabled={uploadingFile}
                            className="flex-1"
                          >
                            {uploadingFile ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                {uploadedFile ? uploadedFile.name : "Choose File (.txt, .docx, .pdf)"}
                              </>
                            )}
                          </Button>
                          {uploadedFile && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setUploadedFile(null);
                                setBookContent("");
                              }}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-stone-500">
                          Supported: .txt, .doc, .docx, .pdf
                        </p>
                      </div>

                      {/* Or paste content */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-stone-50 px-2 text-stone-500">Or paste content</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="book-content">Book Content</Label>
                        <Textarea
                          id="book-content"
                          placeholder="Paste your book content here (first few chapters are enough for generating a back cover summary)..."
                          value={bookContent}
                          onChange={(e) => setBookContent(e.target.value)}
                          rows={4}
                          className="font-mono text-sm"
                        />
                        {bookContent && (
                          <p className="text-xs text-green-600">
                            ✓ {bookContent.length.toLocaleString()} characters loaded
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Book Title *</Label>
                      <Input
                        id="title"
                        placeholder="Enter book title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={coverSource === "dtr" && selectedDTRBook}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="author">Author Name *</Label>
                      <Input
                        id="author"
                        placeholder="Enter author name"
                        value={authorName}
                        onChange={(e) => setAuthorName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Genre *</Label>
                      <Select 
                        value={genre} 
                        onValueChange={setGenre}
                        disabled={coverSource === "dtr" && selectedDTRBook && selectedDTRBook.genre}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select genre" />
                        </SelectTrigger>
                        <SelectContent>
                          {genres.map((g) => (
                            <SelectItem key={g} value={g}>{g}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Mood/Style *</Label>
                      <Select value={mood} onValueChange={setMood}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select mood" />
                        </SelectTrigger>
                        <SelectContent>
                          {moods.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="custom">Custom Details (Optional)</Label>
                    <Textarea
                      id="custom"
                      placeholder="Describe specific elements you want on the cover (e.g., 'Include a lighthouse on a cliff', 'Use purple and gold colors')"
                      value={customDetails}
                      onChange={(e) => setCustomDetails(e.target.value)}
                      rows={2}
                    />
                  </div>

                  {/* Back Cover Section */}
                  <div className="space-y-4 p-4 border rounded-lg bg-stone-50">
                    <h3 className="font-semibold flex items-center gap-2">
                      <FileText className="h-5 w-5 text-amber-600" />
                      Back Cover Content
                    </h3>
                    
                    <RadioGroup value={backCoverType} onValueChange={setBackCoverType}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="summary" id="summary" />
                        <Label htmlFor="summary" className="cursor-pointer">
                          Book Summary (AI-generated)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="bio" id="bio" />
                        <Label htmlFor="bio" className="cursor-pointer">
                          Author&apos;s Bio (you provide)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="custom" id="custom-back" />
                        <Label htmlFor="custom-back" className="cursor-pointer">
                          Custom Text (full control)
                        </Label>
                      </div>
                    </RadioGroup>

                    {/* Summary source for imported books */}
                    {backCoverType === "summary" && coverSource === "imported" && (
                      <div className="mt-3">
                        <Label>Paste Book Content for Summary</Label>
                        <Textarea
                          placeholder="Paste your book content here (first few chapters are enough for a good summary)"
                          value={bookContent}
                          onChange={(e) => setBookContent(e.target.value)}
                          rows={4}
                          className="mt-2"
                        />
                      </div>
                    )}

                    {/* DTR book summary info */}
                    {backCoverType === "summary" && coverSource === "dtr" && selectedDTRBook && (
                      <p className="text-sm text-green-600 flex items-center gap-1 mt-2">
                        <CheckCircle className="h-4 w-4" />
                        Summary will be generated from &quot;{selectedDTRBook.title}&quot;
                      </p>
                    )}

                    {backCoverType === "bio" && (
                      <Textarea
                        placeholder="Enter your author bio (e.g., 'John Smith is a bestselling author of...')"
                        value={backCoverContent}
                        onChange={(e) => setBackCoverContent(e.target.value)}
                        rows={4}
                        className="mt-3"
                      />
                    )}

                    {backCoverType === "custom" && (
                      <Textarea
                        placeholder="Enter your custom back cover text"
                        value={backCoverContent}
                        onChange={(e) => setBackCoverContent(e.target.value)}
                        rows={4}
                        className="mt-3"
                      />
                    )}
                  </div>

                  {/* Credit Info */}
                  <div className="p-4 bg-stone-100 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Cost:</span>
                      <span className="text-lg font-bold text-amber-700">1 Credit</span>
                    </div>
                    <div className="flex justify-between items-center mt-1 text-sm text-stone-600">
                      <span>Your balance:</span>
                      <span>{userCredits} Credits</span>
                    </div>
                    {userCredits < 1 && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        Insufficient credits
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowNewCover(false);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreateCover}
                      disabled={creating || userCredits < 1 || (coverSource === "dtr" && !selectedDTRBook)}
                      className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Covers
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Cover Detail Dialog */}
          <Dialog open={showCoverDetail} onOpenChange={setShowCoverDetail}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <ImageIcon className="h-6 w-6 text-amber-600" />
                      {selectedProject?.title}
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-4 mt-2">
                      {getStatusBadge(selectedProject?.status)}
                      <span>•</span>
                      <span>by {selectedProject?.author_name}</span>
                      <span>•</span>
                      <span>{selectedProject?.genre}</span>
                    </DialogDescription>
                  </div>
                  <div className="flex gap-2">
                    {selectedProject?.status === "completed" && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRegenerate(selectedProject?.id);
                        }}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Regenerate (Free)
                      </Button>
                    )}
                    {selectedProject?.status === "generating" && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => refreshProject(selectedProject.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Refresh
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={(e) => handleDeleteCover(selectedProject.id, e)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                    {selectedProject?.status === "error" && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={(e) => handleDeleteCover(selectedProject.id, e)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </DialogHeader>
              
              {selectedProject?.status === "generating" ? (
                <div className="flex-1 flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="h-16 w-16 animate-spin text-amber-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Generating Your Covers</h3>
                    <p className="text-stone-600 mb-4">{selectedProject?.progress || "This may take a few minutes..."}</p>
                    <p className="text-sm text-stone-500">If this seems stuck, you can delete it and try again.</p>
                  </div>
                </div>
              ) : selectedProject?.status === "completed" ? (
                <div className="flex-1 overflow-y-auto">
                  {/* Save Instructions Banner */}
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4 mx-2 mt-2">
                    <p className="text-amber-800 text-center font-medium">
                      💡 Right Click Image To Save Image As
                    </p>
                  </div>
                  <div className="space-y-6 p-2">
                    {selectedProject?.covers?.map((cover, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-stone-50">
                        <h4 className="font-semibold mb-3">Variation {cover.variation || idx + 1}</h4>
                        <div className="grid grid-cols-3 gap-4">
                          {/* Front Cover */}
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-center">Front Cover</p>
                            <div className="aspect-[2/3] bg-white rounded-lg shadow-md overflow-hidden">
                              <LazyImage 
                                imageId={cover.front_image_id}
                                alt={`Front Cover ${idx + 1}`}
                              />
                            </div>
                          </div>
                          
                          {/* Spine */}
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-center">Spine</p>
                            <div className="aspect-[1/6] mx-auto bg-white rounded-lg shadow-md overflow-hidden" style={{ maxWidth: '60px' }}>
                              <LazyImage 
                                imageId={cover.spine_image_id}
                                alt={`Spine ${idx + 1}`}
                              />
                            </div>
                            <p className="text-xs text-center text-stone-500 mt-2">
                              Title: {selectedProject.title}
                            </p>
                          </div>
                          
                          {/* Back Cover */}
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-center">Back Cover</p>
                            <div className="aspect-[2/3] bg-white rounded-lg shadow-md overflow-hidden">
                              <LazyImage 
                                imageId={cover.back_image_id}
                                alt={`Back Cover ${idx + 1}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Back Cover Text */}
                    {selectedProject?.back_cover_content && (
                      <div className="border rounded-lg p-4 bg-amber-50">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Back Cover Text
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(selectedProject.back_cover_content);
                                toast.success("Text copied!");
                              } catch (err) {
                                // Fallback for environments where clipboard API doesn't work
                                const textArea = document.createElement("textarea");
                                textArea.value = selectedProject.back_cover_content;
                                textArea.style.position = "fixed";
                                textArea.style.left = "-999999px";
                                document.body.appendChild(textArea);
                                textArea.select();
                                try {
                                  document.execCommand("copy");
                                  toast.success("Text copied!");
                                } catch (e) {
                                  toast.error("Failed to copy. Please select and copy manually.");
                                }
                                document.body.removeChild(textArea);
                              }
                            }}
                          >
                            Copy
                          </Button>
                        </h4>
                        <p className="text-sm text-stone-700 whitespace-pre-wrap">
                          {selectedProject.back_cover_content}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center py-12">
                  <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Generation Failed</h3>
                    <p className="text-stone-600">{selectedProject?.error_message || "An error occurred."}</p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Regenerate Dialog */}
          <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-amber-600" />
                  Regenerate Covers
                </DialogTitle>
                <DialogDescription>
                  Add any additional details to refine your new cover designs (free)
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Current Project Info */}
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-sm font-medium text-stone-900">{selectedProject?.title}</p>
                  <p className="text-xs text-stone-500">by {selectedProject?.author_name} • {selectedProject?.genre}</p>
                </div>

                {/* Additional Details Field */}
                <div className="space-y-2">
                  <Label htmlFor="regenerate-details">Additional Details (Optional)</Label>
                  <Textarea
                    id="regenerate-details"
                    placeholder="Add specific instructions for the new covers, e.g.:
• 'Make the colors more vibrant'
• 'Add a sunset in the background'
• 'Use a darker, more mysterious tone'
• 'Include a silhouette of a person'"
                    value={regenerateDetails}
                    onChange={(e) => setRegenerateDetails(e.target.value)}
                    rows={5}
                    className="resize-none"
                  />
                  <p className="text-xs text-stone-500">
                    These details will be combined with your original settings to create new variations.
                  </p>
                </div>

                {/* Credit Info */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-green-900">Cost:</span>
                  <span className="text-lg font-bold text-green-700">FREE</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowRegenerateDialog(false)}
                  className="flex-1"
                  disabled={isRegenerating}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmRegenerate}
                  disabled={isRegenerating}
                  className="flex-1 bg-amber-900 hover:bg-amber-800 text-white"
                >
                  {isRegenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate Covers
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default CoverCreator;
